/**
 * Bulk-email a selected batch of leads — picks a template (or custom), shows
 * an individualised preview for the FIRST selected lead so the admin can see
 * how variable substitution lands, then fires off the batch sequentially via
 * `POST /api/leads/bulk-email`. Default sender is hello@helioses.co.uk.
 *
 * Usage:
 *   <BulkEmailLeadsDialog
 *     open={open}
 *     onOpenChange={setOpen}
 *     selectedLeads={[{id, email, name, first_name, last_name, phone}, ...]}
 *     leadSource="wix"        // "meta" | "wix" | "email" — used for email_log
 *     onComplete={() => fetchLeads()}
 *   />
 */
import React, { useState, useEffect, useMemo } from 'react';
import api, { emailTemplatesAPI } from '../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { Send, Loader2, CheckCircle2, AlertCircle, Users, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { STATUS_DISPLAY_NAMES } from '../lib/utils';
import { EmailModeToggle } from './EmailModeToggle';
import { EmailPreviewDialog } from './EmailPreviewDialog';
import { resolveBodyHtml } from '../lib/emailEditor';

const DEFAULT_FROM = 'hello@helioses.co.uk';

// Mirrors the backend variable substitution so the in-UI preview matches
// what the server will actually send. Keep these in lockstep with
// /app/backend/routes/customers.py::bulk_email_leads.
function substituteVars(text, lead) {
  if (!text) return '';
  const name = (lead.name || '').trim();
  const firstName = (lead.first_name || (name.split(' ')[0] || '')).trim();
  const lastName = (lead.last_name || (name.split(' ').slice(1).join(' ') || '')).trim();
  return text
    .replaceAll('{{first_name}}', firstName || 'there')
    .replaceAll('{{last_name}}', lastName)
    .replaceAll('{{name}}', name || firstName || 'there')
    .replaceAll('{{email}}', lead.email || '')
    .replaceAll('{{phone}}', lead.phone || '');
}

export const BulkEmailLeadsDialog = ({
  open, onOpenChange, selectedLeads = [], leadSource = '', onComplete,
}) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('none');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [fromEmail, setFromEmail] = useState(DEFAULT_FROM);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  const [editorMode, setEditorMode] = useState('text');
  const [previewOpen, setPreviewOpen] = useState(false);

  // Lead chosen for the live preview. Defaults to the first valid lead.
  const validLeads = useMemo(
    () => selectedLeads.filter(l => (l.email || '').includes('@')),
    [selectedLeads],
  );
  const invalidCount = selectedLeads.length - validLeads.length;
  const [previewIdx, setPreviewIdx] = useState(0);
  const previewLead = validLeads[previewIdx] || validLeads[0] || null;

  useEffect(() => {
    if (open) {
      loadTemplates();
      setSelectedTemplate('none');
      setSubject('');
      setBody('');
      setResults(null);
      setPreviewIdx(0);
      setFromEmail(DEFAULT_FROM);
      setEditorMode('text');
    }
  }, [open]);

  const loadTemplates = async () => {
    try {
      const res = await emailTemplatesAPI.getAll();
      setTemplates(res.data || []);
    } catch {/* ignore */}
  };

  const handleTemplateChange = (val) => {
    setSelectedTemplate(val);
    if (val === 'none') {
      setSubject('');
      setBody('');
      return;
    }
    const tmpl = templates.find(t => t.trigger === val);
    if (tmpl) {
      setSubject(tmpl.subject || '');
      setBody(tmpl.body_html || '');
      // Templates are authored as HTML — flip the editor so admin sees markup
      setEditorMode('html');
    }
  };

  const previewSubject = previewLead ? substituteVars(subject, previewLead) : subject;
  const previewBodyRaw = previewLead ? substituteVars(body, previewLead) : body;
  const previewBody = resolveBodyHtml(previewBodyRaw, editorMode);

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    if (validLeads.length === 0) {
      toast.error('No selected leads have a valid email address');
      return;
    }
    setSending(true);
    setResults(null);
    try {
      // Strip raw lead objects down to just what the bulk endpoint needs —
      // avoids leaking sensitive lead fields back to the server in the
      // request body.
      const trimmedLeads = validLeads.map(l => ({
        id: l.id || '',
        email: l.email || '',
        name: l.name || '',
        first_name: l.first_name || '',
        last_name: l.last_name || '',
        phone: l.phone || '',
      }));
      const { data } = await api.post('/leads/bulk-email', {
        leads: trimmedLeads,
        lead_source: leadSource,
        // Send the raw template trigger so the BACKEND re-resolves the
        // template (single source of truth). Subject/body fields below are
        // only used as a fallback when no template is chosen.
        template_trigger: selectedTemplate !== 'none' ? selectedTemplate : '',
        custom_subject: selectedTemplate === 'none' ? subject : '',
        custom_body: selectedTemplate === 'none' ? resolveBodyHtml(body, editorMode) : '',
        from_email: fromEmail || '',
        cc_emails: '',
      });
      setResults(data);
      if (data.sent > 0) {
        toast.success(`Sent ${data.sent} email${data.sent === 1 ? '' : 's'}`);
      }
      if (data.failed?.length) {
        toast.error(`${data.failed.length} failed — see details below`);
      }
      onComplete?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk send failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="bulk-email-leads-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Bulk Email — {validLeads.length} recipient{validLeads.length === 1 ? '' : 's'}
          </DialogTitle>
          <DialogDescription>
            Each lead will receive an <strong>individual</strong> email (sent one at a time,
            ~0.6s apart). Variables like <code className="text-xs bg-slate-100 px-1 rounded">{`{{first_name}}`}</code>
            {' '}are replaced per-lead.
          </DialogDescription>
        </DialogHeader>

        {invalidCount > 0 && (
          <div className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 rounded">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              {invalidCount} selected lead{invalidCount === 1 ? '' : 's'} ha{invalidCount === 1 ? 's' : 've'} no valid email and will be skipped.
            </span>
          </div>
        )}

        {results ? (
          // ===== Post-send results pane =====
          <div className="space-y-3" data-testid="bulk-email-results">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-center">
                <div className="text-2xl font-bold text-emerald-700">{results.sent}</div>
                <div className="text-xs text-emerald-600 uppercase tracking-wide">Sent</div>
              </div>
              <div className="bg-rose-50 border border-rose-200 rounded p-3 text-center">
                <div className="text-2xl font-bold text-rose-700">{results.failed?.length || 0}</div>
                <div className="text-xs text-rose-600 uppercase tracking-wide">Failed</div>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded p-3 text-center">
                <div className="text-2xl font-bold text-slate-700">{results.skipped?.length || 0}</div>
                <div className="text-xs text-slate-600 uppercase tracking-wide">Skipped</div>
              </div>
            </div>
            {(results.failed?.length > 0 || results.skipped?.length > 0) && (
              <ScrollArea className="max-h-48 border rounded">
                <div className="p-2 text-xs space-y-1">
                  {results.failed?.map((f, i) => (
                    <div key={`f-${i}`} className="flex items-start gap-2 text-rose-700">
                      <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                      <span><strong>{f.email}</strong> — {f.error}</span>
                    </div>
                  ))}
                  {results.skipped?.map((s, i) => (
                    <div key={`s-${i}`} className="flex items-start gap-2 text-slate-600">
                      <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                      <span>{s.email || '(no email)'} — {s.reason}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
            <div className="flex justify-end">
              <Button
                onClick={() => onOpenChange(false)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="bulk-email-close-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Close
              </Button>
            </div>
          </div>
        ) : (
          // ===== Compose pane =====
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-500">From</Label>
                <Select
                  value={fromEmail || '__default__'}
                  onValueChange={(v) => setFromEmail(v === '__default__' ? '' : v)}
                >
                  <SelectTrigger data-testid="bulk-email-from"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hello@helioses.co.uk">hello@helioses.co.uk</SelectItem>
                    <SelectItem value="info@helioses.co.uk">info@helioses.co.uk</SelectItem>
                    <SelectItem value="noreply@helioses.co.uk">noreply@helioses.co.uk</SelectItem>
                    <SelectItem value="__default__">Default mailbox</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-500">Template</Label>
                <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
                  <SelectTrigger data-testid="bulk-email-template"><SelectValue placeholder="Pick a template..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No template (custom)</SelectItem>
                    {templates.filter(t => t.active !== false).map(t => (
                      <SelectItem key={t.trigger} value={t.trigger}>
                        {STATUS_DISPLAY_NAMES[t.trigger] || t.label || t.trigger}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-500">
                Subject <span className="text-slate-400">(supports template vars)</span>
              </Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. Hi {{first_name}}, an update from Helios..."
                data-testid="bulk-email-subject"
              />
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium text-slate-500">Body</Label>
                <EmailModeToggle
                  mode={editorMode}
                  onChange={setEditorMode}
                  body={body}
                  setBody={setBody}
                  testIdPrefix="bulk-editor-mode"
                />
              </div>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder={editorMode === 'html'
                  ? '<p>Dear {{first_name}},</p>\n\n<p>Write the email body once...</p>'
                  : 'Dear {{first_name}},\n\nWrite the email body once — it will be personalised per recipient.\n\nKind regards,\nHelios Electrical & Solar'}
                className={`w-full min-h-[140px] p-3 text-sm border border-slate-200 rounded-lg resize-y focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${editorMode === 'html' ? 'font-mono bg-slate-900 text-emerald-300' : 'bg-white'}`}
                spellCheck={editorMode === 'text'}
                data-testid="bulk-email-body"
              />
              <p className="text-xs text-slate-400">
                {editorMode === 'html' ? 'HTML mode — paste markup or hand-write it.' : 'Plain text — line breaks become paragraphs.'}{' '}
                Vars: <code>{`{{first_name}}`}</code>, <code>{`{{last_name}}`}</code>, <code>{`{{name}}`}</code>, <code>{`{{email}}`}</code>, <code>{`{{phone}}`}</code>.
              </p>
            </div>

            {/* Per-lead preview — shows the FINAL rendered email for a chosen
                recipient so the admin sanity-checks variable substitution
                before firing the batch. */}
            {previewLead && (subject || body) && (
              <div className="border border-blue-200 bg-blue-50/40 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-xs font-medium text-blue-700 flex items-center gap-1">
                    <Eye className="w-3.5 h-3.5" /> Preview for
                  </Label>
                  <Select
                    value={String(previewIdx)}
                    onValueChange={(v) => setPreviewIdx(parseInt(v, 10) || 0)}
                  >
                    <SelectTrigger className="h-7 text-xs w-56" data-testid="bulk-email-preview-picker">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {validLeads.slice(0, 25).map((l, i) => (
                        <SelectItem key={l.id || l.email || i} value={String(i)}>
                          {(l.name || `${l.first_name || ''} ${l.last_name || ''}`.trim() || l.email).slice(0, 50)}
                        </SelectItem>
                      ))}
                      {validLeads.length > 25 && (
                        <SelectItem value="25" disabled>+ {validLeads.length - 25} more...</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-xs">
                  <div className="text-slate-500">To: <strong className="text-slate-700">{previewLead.email}</strong></div>
                  <div className="text-slate-500">Subject: <strong className="text-slate-800">{previewSubject || <em className="text-slate-400">(empty)</em>}</strong></div>
                </div>
                <div
                  className="text-sm text-slate-700 bg-white border border-slate-200 rounded p-2 max-h-36 overflow-auto"
                  data-testid="bulk-email-preview-body"
                  dangerouslySetInnerHTML={{ __html: previewBody || '<em>(empty)</em>' }}
                />
              </div>
            )}

            <div className="flex justify-end gap-3 pt-1">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={() => setPreviewOpen(true)}
                disabled={!subject.trim() && !body.trim()}
                data-testid="bulk-email-preview-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                Preview
              </Button>
              <Button
                onClick={handleSend}
                disabled={sending || !subject.trim() || !body.trim() || validLeads.length === 0}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="bulk-email-send-btn"
              >
                {sending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending {validLeads.length}...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Send to {validLeads.length}</>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    <EmailPreviewDialog
      open={previewOpen}
      onOpenChange={setPreviewOpen}
      to={previewLead?.email}
      from={fromEmail}
      subject={previewSubject}
      bodyHtml={previewBody}
      onConfirmSend={handleSend}
      sending={sending}
      recipientLabel={previewLead?.first_name || previewLead?.name?.split(' ')[0] || 'the lead'}
    />
    </>
  );
};

export default BulkEmailLeadsDialog;
